/**
 * Хранение состояния отправки ИНН в localStorage.
 *
 * После успешной отправки ИНН клиент видит, что ИНН на проверке, и не может
 * отправить его повторно, пока не пройдут `RESUBMIT_DELAY_MS` (24 часа).
 * Состояние хранится отдельно для каждого клиента, чтобы данные не пересекались
 * при входе под разными аккаунтами в одном браузере.
 */

const STORAGE_KEY_PREFIX = 'inn-submission';

/** Через сколько после отправки можно отправить ИНН повторно */
export const RESUBMIT_DELAY_MS = 24 * 60 * 60 * 1000;

export interface InnSubmission {
    /** Отправленный ИНН */
    taxId: string;
    /** Дата отправки в формате ISO */
    submittedAt: string;
}

const getStorageKey = (customerId?: number): string => `${STORAGE_KEY_PREFIX}:${customerId ?? 'unknown'}`;

/** Возвращает сохраненную отправку ИНН или null, если ИНН не отправляли */
export const getInnSubmission = (customerId?: number): InnSubmission | null => {
    if (typeof window === 'undefined') return null;

    try {
        const raw = localStorage.getItem(getStorageKey(customerId));
        if (!raw) return null;

        const parsed = JSON.parse(raw) as Partial<InnSubmission>;
        if (!parsed?.submittedAt) return null;

        return { taxId: parsed.taxId ?? '', submittedAt: parsed.submittedAt };
    } catch {
        return null;
    }
};

/** Сохраняет факт отправки ИНН и возвращает сохраненное состояние */
export const saveInnSubmission = (customerId: number | undefined, taxId: string): InnSubmission => {
    const submission: InnSubmission = { taxId, submittedAt: new Date().toISOString() };

    try {
        localStorage.setItem(getStorageKey(customerId), JSON.stringify(submission));
    } catch {
        // localStorage может быть недоступен (приватный режим) — состояние живет только в памяти
    }

    return submission;
};

/** Удаляет сохраненную отправку (например, когда ИНН уже появился в профиле) */
export const clearInnSubmission = (customerId?: number): void => {
    try {
        localStorage.removeItem(getStorageKey(customerId));
    } catch {
        // ignore
    }
};

/** Прошло ли достаточно времени с момента отправки, чтобы отправить ИНН повторно */
export const canResubmitInn = (submission: InnSubmission | null): boolean => {
    if (!submission) return true;

    const submittedAt = new Date(submission.submittedAt).getTime();
    if (Number.isNaN(submittedAt)) return true;

    return Date.now() - submittedAt >= RESUBMIT_DELAY_MS;
};
